import { Component, DoCheck, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FamilyCodes } from 'src/app/core/_enums/family.code';
import { GlobalConstants } from 'src/constants/global.constants';

export type BadgeStatutItemType = 'default' | 'blue' | 'green' | 'project' | 'structure' | 'project-list' | 'formula' | 'operation-product' | 'atelier' | 'engin' | 'work-inspection-item' | 'industry' | 'acheminement';

interface StateMapping {
  [key: string]: string;
}

@Component({
  selector: 'badge-statut',
  templateUrl: './badge-statut.component.html',
  styleUrls: ['./badge-statut.component.scss']
})
export class BadgeStatutComponent implements OnInit, DoCheck {
  variant: string = 'vert';
  justificativeCount: number = 0;

  @Input() itemType?: BadgeStatutItemType = 'default';
  @Input() item: any;

  private readonly projectListStateMapping: StateMapping = {
    Pending: 'orange',
    Finished: 'vert',
    Model: 'gris',
    Handed: 'bleu',
    Archived: 'noir'
  };

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.evaluateVariant();
  }

  ngDoCheck(): void {
    this.evaluateVariant();
  }

  private evaluateVariant(): void {
    if (!this.item) {
      this.variant = 'vert';
      return;
    }

    if (this.item.isImportedFrom) {
      this.variant = 'marron';
      return;
    }

    this.handleItemType();
  }

  private handleItemType(): void {
    switch (this.itemType) {
      case 'project-list':
        this.handleProjectListItemType();
        break;
      case 'work-inspection-item':
        this.handleWorkInspectionItemType();
        break;
      case 'industry':
        this.handleIndustryItemType();
        break;
      case 'blue':
        this.variant = 'bleu';
        break;
      case 'green':
        this.variant = 'vert';
        break;
      default:
        this.handleDefaultItemType();
        break;
    }
  }

  private handleWorkInspectionItemType(): void {
    this.variant = 'jaune';
  }

  private handleIndustryItemType(): void {
    this.variant = this.isUserResource() ? 'bleu' : 'vert';
  }

  private handleDefaultItemType(): void {
    if (this.isUserResource()) {
      const needsJustification =
        this.item.familyCode !== FamilyCodes.DefaultTransportationFamily &&
        !this.item.projectType &&
        this.itemType !== 'formula' &&
        this.itemType !== 'atelier' &&
        this.itemType !== 'acheminement' &&
        !(this.itemType === 'operation-product' && (this.item.detailedResource || this.item.isAtelier));
      if (!this.item.fCeuFileName && !this.item.fileCalculCeuName && !this.item.fileName && !this.item.justified && needsJustification) {
        this.variant = 'rouge';
        return;
      }

      this.variant = 'bleu';
    } else {
      this.variant = 'vert';
    }
  }

  private handleProjectListItemType(): void {
    if (this.item?.state === 'Pending') {
      this.variant = this.item?.isPublished ? 'orange' : 'jaune';
    } else {
      this.variant = this.projectListStateMapping[this.item?.state] || 'vert';
    }
  }

  private isUserResource(): boolean {
    return !this.item.ruade || (this.item.ownerId && this.item.ownerId !== GlobalConstants.EMPTY_GUID);
  }

  getTooltipText(): string {
    if (this.itemType === 'work-inspection-item') {
      if (this.justificativeCount > 0) {
        return this.translate.instant('tooltip_justificatives_attached', { count: this.justificativeCount });
      } else {
        return this.translate.instant('tooltip_no_justificative_attached');
      }
    }

    if (this.itemType === 'green') {
      return '';
    }

    const tooltipMapping: { [key: string]: string } = {
      bleu: this.translate.instant('tooltip_resource_created_by_user'),
      rouge: this.translate.instant('tooltip_resource_without_justification'),
      vert: this.translate.instant('tooltip_resource_from_seve'),
      marron: this.translate.instant('tooltip_resource_imported'),
      orange: this.translate.instant('tooltip_resource_missing_justification')
    };

    return tooltipMapping[this.variant] || '';
  }
}
